// Utility functions for 12-hour AM/PM time formatting and direct phone messaging (SMS / WhatsApp / Push Notifications)

/**
 * Converts a 24-hour time string like "21:00", "09:30", "21:00:00" or raw string into 12-hour AM/PM format (e.g. "9:00 PM").
 */
export function formatTimeTo12Hour(timeStr?: string): string {
  if (!timeStr) return '';
  const trimmed = timeStr.trim();

  // If already contains AM or PM, return capitalized
  if (/am|pm/i.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  // Split HH:MM or HH:MM:SS
  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;

  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];

  if (isNaN(hours)) return trimmed;

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 becomes 12

  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Formats start and end times into a readable range e.g. "9:00 PM - 10:00 PM"
 */
export function formatTimeRange(startTime?: string, endTime?: string): string {
  if (!startTime) return '';
  const formattedStart = formatTimeTo12Hour(startTime);
  if (!endTime) return formattedStart;
  const formattedEnd = formatTimeTo12Hour(endTime);
  return `${formattedStart} - ${formattedEnd}`;
}

/**
 * Formats a YYYY-MM-DD date string into a friendly localized string like "Friday, 14 Aug"
 */
export function formatDateFriendly(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return dateStr;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  } catch (e) {
    return dateStr;
  }
}

/**
 * Generates direct SMS URI link (sms:+88017... ?body=...)
 */
export function generateSmsReminderUrl(phone: string, matchTitle: string, dateStr: string, timeStr: string, venueName: string): string {
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  const time12 = formatTimeTo12Hour(timeStr);
  const dateFormatted = formatDateFriendly(dateStr);
  const message = `⚽ GigaBalls FC Matchday Alert!\n\nMatch: ${matchTitle}\nDate: ${dateFormatted}\nTime: ${time12}\nVenue: ${venueName}\n\nPlease confirm your RSVP on the app now!`;
  
  return `sms:${cleanPhone}?body=${encodeURIComponent(message)}`;
}

/**
 * Generates direct WhatsApp Web/App chat link
 */
export function generateWhatsappReminderUrl(phone: string, matchTitle: string, dateStr: string, timeStr: string, venueName: string): string {
  const cleanPhone = phone.replace(/[^\d]/g, ''); // WhatsApp requires numeric country code without '+'
  const time12 = formatTimeTo12Hour(timeStr);
  const dateFormatted = formatDateFriendly(dateStr);
  const message = `⚽ *GigaBalls FC Matchday Alert!*\n\n*Match:* ${matchTitle}\n*Date:* ${dateFormatted}\n*Time:* ${time12}\n*Venue:* ${venueName}\n\n👉 Please confirm your RSVP on the squad app!`;

  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
}

/**
 * Requests browser Push Notification permission if available
 */
export async function requestPushNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    alert('Browser Push Notifications are not supported on this device. Direct SMS/WhatsApp messaging will be used instead.');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

/**
 * Triggers an immediate browser Push Notification
 */
export function sendLocalPushNotification(title: string, body: string, icon = '/icon.png') {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(title, {
        body,
        icon,
        badge: icon,
      });
    } catch (e) {
      console.error('Push Notification error:', e);
    }
  }
}
